<template>
  <div>
    <article class="tw-relative">
      <div class="tw-flex tw-gap-3">
        <NuxtLink
          :to="'/user/' + comment.user_id"
          :external="mainStore.isLoadedFromApp"
          class="tw-h-10 tw-w-10 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-full"
        >
          <RelationshipUserImage
            v-if="user"
            :user="user"
            thumb
            class="tw-h-full tw-w-full tw-object-cover"
          />
        </NuxtLink>

        <div class="tw-flex-grow">
          <div class="tw-relative tw-rounded-lg tw-bg-black/5 tw-px-4 tw-py-2">
            <div class="tw-flex tw-justify-between">
              <NuxtLink
                :to="'/user/' + comment.user_id"
                :external="mainStore.isLoadedFromApp"
                class="tw-text-sm"
              >
                <span class="tw-font-bold">
                  <template v-if="user"> {{ user.firstname }} {{ user.lastname }} </template>

                  <template v-else> - </template>
                </span>
              </NuxtLink>

              <div>
                <Dropdown v-if="isOwner || (showReportButton && !isOwner)">
                  <template #header>
                    <InputAppButton class="tw-cursor-pointer">
                      <FontAwesomeIcon icon="ellipsis-h" class="tw-text-black/50" />
                    </InputAppButton>
                  </template>

                  <template #content>
                    <div class="card padding tw-absolute tw-right-0 tw-top-full">
                      <InputAppButton
                        v-if="isOwner"
                        class="tw-cursor-pointer"
                        @click="state.deleteQuery = true"
                      >
                        {{ t('Delete') }}
                      </InputAppButton>

                      <InputAppButton
                        v-if="showReportButton && !isOwner"
                        class="tw-cursor-pointer"
                        @click="emit('report', comment.id)"
                      >
                        {{ t('Report') }}
                      </InputAppButton>
                    </div>
                  </template>
                </Dropdown>
              </div>
            </div>

            <div class="comment-content">
              <div>
                <p
                  :key="comment.text"
                  v-linkify:options="{
                    target: '_blank'
                  }"
                  class="linkified-text tw-text-sm"
                  style="overflow-wrap: anywhere"
                >
                  {{ comment.text }}
                </p>
              </div>
            </div>
          </div>

          <span class="tw-text-xs tw-leading-none tw-text-riser-mainText/50">
            {{ formatTimeDifference(new Date(comment.created_at)) }}
          </span>
        </div>
      </div>
    </article>

    <!-- DELETE CONFIRMATION popup -->
    <ConfirmDialog
      v-if="state.deleteQuery"
      :title="t('Are-you-sure-you-want-to-delete-your-comment?')"
      :options="[
        {
          text: t('Yes'),
          onClick: () => deleteComment(comment.id),
          class: 'tw-bg-riser-red tw-text-white tw-py-2 tw-rounded-lg'
        },
        {
          text: t('No'),
          onClick: () => (state.deleteQuery = false),
          class: 'tw-bg-gray-200 tw-py-2 tw-rounded-lg'
        }
      ]"
      @close="state.deleteQuery = false"
    />
  </div>
</template>

<script setup lang="ts">
import type { PostingComment } from '~/types/postings'

const props = defineProps<{
  showReportButton?: boolean
  comment: PostingComment
  isLikeable?: boolean
  endpoint: string
}>()

const state = reactive({
  loading: false,
  deleteQuery: false,
  message: props.comment.text
})

const emit = defineEmits<{
  deleted: [id: number]
  report: [commentId: number]
}>()

const authStore = useAuthStore()
const mainStore = useMainStore()
const { t } = useI18n()

const { deleteComment: deleteCommentInternal } = useCommentsService()

const user = useUser(props.comment.user_id)

const isOwner = computed(() => {
  return authStore.session_data.user.id === props.comment.user_id
})

const deleteComment = async (id: number) => {
  const endPoint = props.endpoint ? props.endpoint + '/' + id : 'comments/' + id
  await deleteCommentInternal(endPoint)
  emit('deleted', id)
  state.deleteQuery = false
}
</script>

<style scoped>
.linkified-text :deep(a) {
  @apply tw-text-riser-blue;
}
</style>
